/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// Run replaceTM as early as possible
function replaceTM() {
  document.querySelectorAll('split-lines span').forEach(function(el) {
    el.childNodes.forEach(function(node) {
      if (node.nodeType === 3 && node.textContent.includes('™')) {
        const sup = document.createElement('sup');
        sup.className = 'tm-symbol';
        sup.textContent = '™';
        const parts = node.textContent.split('™');
        const fragment = document.createDocumentFragment();
        parts.forEach(function(part, i) {
          fragment.appendChild(document.createTextNode(part));
          if (i < parts.length - 1) {
            fragment.appendChild(sup.cloneNode(true));
          }
        });
        node.parentNode.replaceChild(fragment, node);
      }
    });
  });
}

const observer = new MutationObserver(function() {
  replaceTM();
});
observer.observe(document.body, { childList: true, subtree: true });
document.addEventListener('DOMContentLoaded', replaceTM);

// ============================================
// Default Lug Size to 20mm
// ============================================
document.addEventListener('DOMContentLoaded', function() {
  const urlParams = new URLSearchParams(window.location.search);
  
  if (!urlParams.has('variant')) {
    const lugOptionNames = ['lug size (mm)', 'lug width (mm)'];
    
    document.querySelectorAll('.product-form__option-name').forEach(function(label) {
      const labelText = label.textContent.trim().replace(':', '').trim().toLowerCase();
      
      if (lugOptionNames.includes(labelText)) {
        const optionSelector = label.closest('.product-form__option-selector');
        if (optionSelector) {
          optionSelector.querySelectorAll('input[type="radio"]').forEach(function(input) {
            const inputLabel = optionSelector.querySelector('label[for="' + input.id + '"]');
            if (inputLabel) {
              const inputText = inputLabel.textContent.trim().toLowerCase();
                //   console.log('Input label text:', inputText);
              if (inputText === '20' || inputText === '20mm' || inputText === '20 mm') {
                input.checked = true;
                input.dispatchEvent(new Event('change', { bubbles: true }));
              }
            }
          });
        }
      }
    });
  }
});

// ============================================
// Slideshow Zoom-Out Effect Reset
// ============================================
const slideshowZoom = document.querySelector('.slideshow--zoom');

if (slideshowZoom) {
  const observer = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      if (mutation.attributeName === 'hidden' && !mutation.target.hasAttribute('hidden')) {
        const images = mutation.target.querySelectorAll('.slideshow__image');
        images.forEach(function(img) {
          img.style.animation = 'none';
          img.offsetHeight; // force reflow
          img.style.animation = '';
        });
      }
    });
  });

  document.querySelectorAll('.slideshow--zoom slide-show-item').forEach(function(slide) {
    observer.observe(slide, { attributes: true });
  });
}

// ============================================
// Buckle Selector
// ============================================
(function() {

  const savedSelections = {};

  function initBuckleSelector(productId, quickBuyContext) {

    // console.log('initBuckleSelector called for:', productId, '| savedSelections:', JSON.stringify(savedSelections));

    const selectorEl = document.getElementById('buckle-selector-' + productId);
    if (!selectorEl) return;

    const dataEl = document.getElementById('buckle-data-' + productId);
    if (!dataEl) return;

    const buckles = JSON.parse(dataEl.textContent);
    const displayMode = dataEl.dataset.displayMode || 'buckles';
    const unavailableMode = dataEl.dataset.unavailableMode || 'show';

    // Track multiple selections
    // Each selection: { buckleId, color, variantId }
    if (!savedSelections[productId]) {
      savedSelections[productId] = [];
    }
    let selections = savedSelections[productId];

    // Get available stock for a variant accounting for existing selections
    function getAvailableStock(variantId) {
      const buckle = buckles.find(function(b) {
        return b.variants.some(function(v) { return v.id === variantId; });
      });
      if (!buckle) return 0;
      const variant = buckle.variants.find(function(v) { return v.id === variantId; });
      if (!variant) return 0;
      if (variant.inventory_policy === 'continue') return 999;
      const selectedCount = selections.filter(function(s) { return s.variantId === variantId; }).length;
      return Math.max(0, (variant.inventory_quantity || 0) - selectedCount);
    }

    // Get current strap size
    function getStrapSize() {
      const sizeEl = document.getElementById('buckle-size-' + productId);
      return sizeEl ? sizeEl.value : null;
    }

    // Find matching variant
    function findMatchingVariant(buckle, color, strapSize) {
      return buckle.variants.find(function(v) {
        const buckleSize = v.size ? v.size.toString().toLowerCase().replace('mm', '').trim() : null;
        const strapSizeClean = strapSize ? strapSize.toString().toLowerCase().replace('mm', '').trim() : null;
        const sizeMatch = buckleSize && strapSizeClean && buckleSize === strapSizeClean;
        const colorMatch = !color || v.color === color;
        return sizeMatch && colorMatch;
      });
    }

    function findFirstAvailableColor(buckle, strapSize) {
      const variant = buckle.variants.find(function(v) {
        const buckleSize = v.size ? v.size.toString().toLowerCase().replace('mm', '').trim() : null;
        const strapSizeClean = strapSize ? strapSize.toString().toLowerCase().replace('mm', '').trim() : null;
        return buckleSize === strapSizeClean && v.available && getAvailableStock(v.id) > 0;
      });
      return variant ? variant.color : null;
    }

    function buckleHasSize(buckle, strapSize) {
      return buckle.variants.some(function(v) {
        const buckleSize = v.size ? v.size.toString().toLowerCase().replace('mm', '').trim() : null;
        const strapSizeClean = strapSize ? strapSize.toString().toLowerCase().replace('mm', '').trim() : null;
        return buckleSize && strapSizeClean && buckleSize === strapSizeClean;
      });
    }

    function getBuckleColors(buckle) {
      const colors = [];
      const seen = {};
      buckle.variants.forEach(function(v) {
        if (v.color && !seen[v.color]) {
          seen[v.color] = true;
          colors.push(v.color);
        }
      });
      return colors;
    }

    function formatMoney(cents) {
      return '$' + (cents / 100).toFixed(2);
    }

    function getSwatchColor(colorName) {
      const swatchMap = {
        'brushed stainless': '#b8b8b8',
        'brushed stainless 316 - limited edition': '#b8b8b8',
        'polished stainless': '#d4d4d4',
        'black buckle': '#222222',
        'gold buckle': '#d4af37',
        'rose gold buckle': '#b76e79'
      };
      return swatchMap[colorName.toLowerCase()] || '#999999';
    }

    // Build the selector list container
    function buildSelectorList() {
      // Remove existing list if any
      const existing = selectorEl.querySelector('.buckle-selector__list');
      if (existing) existing.remove();

      const list = document.createElement('div');
      list.className = 'buckle-selector__list';
      selectorEl.appendChild(list);

      // Render existing selections
      selections.forEach(function(selection, index) {
        addSelectorItem(list, index, selection);
      });

      // Add empty selector at the end
      addSelectorItem(list, selections.length, null);
    }

    function addSelectorItem(list, index, selection) {
      const item = document.createElement('div');
      item.className = 'buckle-selector__item' + (selection ? ' has-selection' : '') + (index > 0 && !selection ? ' is-additional is-visible' : index > 0 ? ' is-additional is-visible' : '');
      item.dataset.index = index;

      const drawerId = 'buckle-drawer-' + productId + '-' + index;
      const popoverId = 'buckle-popover-' + productId + '-' + index;

      // Trigger button
      const desktopBtn = document.createElement('button');
      desktopBtn.type = 'button';
      desktopBtn.className = 'buckle-selector__trigger hidden-phone';
      // desktopBtn.setAttribute('is', 'toggle-button');
      desktopBtn.setAttribute('aria-controls', drawerId);
      desktopBtn.setAttribute('aria-expanded', 'false');

      const mobileBtn = document.createElement('button');
      mobileBtn.type = 'button';
      mobileBtn.className = 'buckle-selector__trigger hidden-lap-and-up';
      // mobileBtn.setAttribute('is', 'toggle-button');
      mobileBtn.setAttribute('aria-controls', popoverId);
      mobileBtn.setAttribute('aria-expanded', 'false');

      // Preview square
      const preview = document.createElement('span');
      preview.className = 'buckle-selector__preview';

      const plus = document.createElement('span');
      plus.className = 'buckle-selector__plus';
      plus.textContent = '+';

      const img = document.createElement('img');
      img.className = 'buckle-selector__image';
      img.src = selection ? (selection.image || '') : '';
      img.alt = '';
      img.style.display = selection ? 'block' : 'none';
      if (selection) plus.style.display = 'none';

      preview.appendChild(plus);
      preview.appendChild(img);

      const label = document.createElement('span');
      label.className = 'buckle-selector__label';
      label.textContent = selection ? selection.label : (index === 0 ? 'Add a buckle/deployant' : 'Add another buckle/deployant');

      desktopBtn.appendChild(preview.cloneNode(true));
      desktopBtn.appendChild(label.cloneNode(true));
      mobileBtn.appendChild(preview.cloneNode(true));
      mobileBtn.appendChild(label.cloneNode(true));

      item.appendChild(desktopBtn);
      item.appendChild(mobileBtn);

      // Remove button
      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'buckle-selector__remove';
      removeBtn.innerHTML = '&times;';
      removeBtn.title = 'Remove buckle';
      removeBtn.addEventListener('click', function() {
        selections.splice(index, 1);
        rebuildSelectors();
      });
      item.appendChild(removeBtn);

      // Hidden variant input
      const variantInput = document.createElement('input');
      variantInput.type = 'hidden';
      variantInput.name = 'buckle_variant_id_' + index;
      variantInput.id = 'buckle-variant-id-' + productId + '-' + index;
      variantInput.value = selection ? selection.variantId : '';
      item.appendChild(variantInput);

      list.appendChild(item);

      // Create drawer and popover for this item
      createTray(drawerId, popoverId, index);

      // Initialize Focal toggle behavior
      initToggleButtons(item, drawerId, popoverId, index);
    }

    function createTray(drawerId, popoverId, index) {
      // Remove existing
      const existingDrawer = document.getElementById(drawerId);
      const existingPopover = document.getElementById(popoverId);
      if (existingDrawer) existingDrawer.remove();
      if (existingPopover) existingPopover.remove();

      // Desktop drawer
      const drawer = document.createElement('drawer-content');
      drawer.id = drawerId;
      drawer.className = 'drawer drawer--large hidden-phone';
      drawer.innerHTML = `
        <span class="drawer__overlay"></span>
        <header class="drawer__header">
          <p class="drawer__title heading h6">Add a Buckle or Deployant</p>
          <button type="button" class="drawer__close-button tap-area" data-action="close" title="Close">
            <svg focusable="false" width="14" height="14" class="icon icon--close" viewBox="0 0 14 14">
              <path d="M13 13L1 1M13 1L1 13" stroke="currentColor" stroke-width="2" fill="none"></path>
            </svg>
          </button>
        </header>
        <div class="drawer__content drawer__content--padded-start">
          <div class="buckle-selector__grid" id="buckle-grid-${drawerId}"></div>
        </div>
      `;
      document.body.appendChild(drawer);
      // Force connectedCallback to re-run for proper initialization
      document.body.removeChild(drawer);
      document.body.appendChild(drawer);

      // Mobile popover
      const popover = document.createElement('popover-content');
      popover.id = popoverId;
      popover.className = 'popover hidden-lap-and-up';
      popover.setAttribute('hidden', '');
      popover.innerHTML = `
        <span class="popover__overlay"></span>
        <header class="popover__header">
          <p class="popover__title heading h6">Add a Buckle or Deployant</p>
          <button type="button" class="popover__close-button tap-area tap-area--large" data-action="close" title="Close">
            <svg focusable="false" width="14" height="14" class="icon icon--close" viewBox="0 0 14 14">
              <path d="M13 13L1 1M13 1L1 13" stroke="currentColor" stroke-width="2" fill="none"></path>
            </svg>
          </button>
        </header>
        <div class="popover__content">
          <div class="buckle-selector__grid" id="buckle-grid-mobile-${popoverId}"></div>
        </div>
      `;
      document.body.appendChild(popover);
      // Force connectedCallback to re-run for proper initialization
      document.body.removeChild(popover);
      document.body.appendChild(popover);

      // Render grid for this tray
      renderTrayGrid(drawerId, popoverId, index, getStrapSize());
    }

    function renderTrayGrid(drawerId, popoverId, index, strapSize) {
      const gridDesktop = document.getElementById('buckle-grid-' + drawerId);
      const gridMobile = document.getElementById('buckle-grid-mobile-' + popoverId);

      let html = '';

      if (displayMode === 'variants') {
        html = buckles.map(function(buckle) {
          return buckle.variants
            .filter(function(v) {
              const buckleSize = v.size ? v.size.toString().toLowerCase().replace('mm', '').trim() : null;
              const strapSizeClean = strapSize ? strapSize.toString().toLowerCase().replace('mm', '').trim() : null;
              const sizeMatch = buckleSize && strapSizeClean && buckleSize === strapSizeClean;
              if (!sizeMatch) return false;
              if (unavailableMode === 'hide' && !v.available) return false;
              return true;
            })
            .sort(function(a, b) {
              if (unavailableMode === 'bottom') {
                const aStock = getAvailableStock(a.id);
                const bStock = getAvailableStock(b.id);
                if (aStock > 0 && bStock <= 0) return -1;
                if (aStock <= 0 && bStock > 0) return 1;
              }
              return 0;
            })
            .map(function(variant) {
              const availableStock = getAvailableStock(variant.id);
              const isUnavailable = !variant.available || availableStock <= 0;
              const image = variant.image || buckle.featured_image;

              return `
                <div class="buckle-card ${isUnavailable ? 'is-unavailable' : ''}"
                     data-buckle-id="${buckle.id}"
                     data-variant-id="${variant.id}"
                     data-color="${variant.color}"
                     data-available="${!isUnavailable}">
                  <img class="buckle-card__image" src="${image || ''}" alt="${buckle.title} - ${variant.color}">
                  <div class="buckle-card__title">${buckle.title}</div>
                  <div class="buckle-card__color">${variant.color}</div>
                  <div class="buckle-card__size">Size: ${variant.size}</div>
                  <div class="buckle-card__price">${formatMoney(variant.price)}</div>
                  ${isUnavailable ? '<div class="buckle-card__unavailable-message">Not available</div>' : ''}
                </div>
              `;
            }).join('');
        }).join('');
      } else {
        html = buckles.map(function(buckle) {
          const hasSize = buckleHasSize(buckle, strapSize);
          const colors = getBuckleColors(buckle);
          const currentColor = hasSize ? findFirstAvailableColor(buckle, strapSize) : colors[0];
          const matchingVariant = currentColor ? findMatchingVariant(buckle, currentColor, strapSize) : null;
          const availableStock = matchingVariant ? getAvailableStock(matchingVariant.id) : 0;
          const isUnavailable = !hasSize || availableStock <= 0;
          const price = matchingVariant ? formatMoney(matchingVariant.price) : '';
          const image = (matchingVariant && matchingVariant.image) || buckle.featured_image;

          return `
            <div class="buckle-card ${isUnavailable ? 'is-unavailable' : ''}"
                 data-buckle-id="${buckle.id}"
                 data-available="${!isUnavailable}">
              <img class="buckle-card__image" src="${image || ''}" alt="${buckle.title}">
              <div class="buckle-card__title">${buckle.title}</div>
              <div class="buckle-card__size">${strapSize ? 'Size: ' + strapSize + 'mm' : ''}</div>
              <div class="buckle-card__price">${price}</div>
              <div class="buckle-card__swatches">
                ${colors.map(function(color) {
                  return `<span class="buckle-swatch"
                                data-color="${color}"
                                data-buckle-id="${buckle.id}"
                                title="${color}"
                                style="background-color: ${getSwatchColor(color)}"></span>`;
                }).join('')}
              </div>
              ${isUnavailable ? '<div class="buckle-card__unavailable-message">Not available in this size</div>' : ''}
            </div>
          `;
        }).join('');
      }

      if (gridDesktop) gridDesktop.innerHTML = html;
      if (gridMobile) gridMobile.innerHTML = html;

      // Attach click events to both grids
      [gridDesktop, gridMobile].forEach(function(grid) {
        if (!grid) return;

        grid.querySelectorAll('.buckle-card').forEach(function(card) {
          card.addEventListener('click', function() {
            const available = card.dataset.available === 'true';
            if (!available) return;

            const buckleId = parseInt(card.dataset.buckleId);
            const buckle = buckles.find(function(b) { return b.id === buckleId; });
            if (!buckle) return;

            let variantId, color, image, label, price;

            if (displayMode === 'variants') {
              variantId = parseInt(card.dataset.variantId);
              color = card.dataset.color;
              const variant = buckle.variants.find(function(v) { return v.id === variantId; });
              image = variant ? (variant.image || buckle.featured_image) : buckle.featured_image;
              price = variant ? formatMoney(variant.price) : '';
              label = buckle.title + ' — ' + color + ' / ' + (variant ? variant.size : '') + ' — ' + price;
            } else {
              color = card.dataset.color || findFirstAvailableColor(buckle, getStrapSize());
              const variant = findMatchingVariant(buckle, color, getStrapSize());
              variantId = variant ? variant.id : null;
              image = variant ? (variant.image || buckle.featured_image) : buckle.featured_image;
              price = variant ? formatMoney(variant.price) : '';
              label = buckle.title + ' — ' + color + ' / ' + (variant ? variant.size : '') + ' — ' + price;
            }

            if (!variantId) return;

            // Update or add selection
            selections[index] = { buckleId, color, variantId, image, label };

            // Close trays
            const drawer = document.getElementById(drawerId);
            const popover = document.getElementById(popoverId);
            if (drawer) drawer.removeAttribute('open');
            if (popover) {
              popover.setAttribute('hidden', '');
              const mobileToggle = document.querySelector('[aria-controls="' + popoverId + '"]');
              if (mobileToggle) mobileToggle.setAttribute('aria-expanded', 'false');
            }

            rebuildSelectors();
          });
        });

        // Swatch clicks for non-variant mode
        grid.querySelectorAll('.buckle-swatch').forEach(function(swatch) {
          swatch.addEventListener('click', function(e) {
            e.stopPropagation();
            const buckleId = parseInt(swatch.dataset.buckleId);
            const color = swatch.dataset.color;
            const buckle = buckles.find(function(b) { return b.id === buckleId; });
            if (!buckle) return;

            const parentCard = swatch.closest('.buckle-card');
            if (parentCard) {
              const variant = findMatchingVariant(buckle, color, getStrapSize());
              const cardImage = parentCard.querySelector('.buckle-card__image');
              if (cardImage && variant && variant.image) cardImage.src = variant.image;

              const cardPrice = parentCard.querySelector('.buckle-card__price');
              if (cardPrice && variant) cardPrice.textContent = formatMoney(variant.price);

              grid.querySelectorAll('.buckle-swatch[data-buckle-id="' + buckleId + '"]').forEach(function(s) {
                s.classList.toggle('is-selected', s.dataset.color === color);
              });
            }
          });
        });
      });
    }

    function initToggleButtons(item, drawerId, popoverId, index) {
      item.querySelectorAll('.buckle-selector__trigger').forEach(function(btn) {
        btn.addEventListener('click', function(e) {
          e.stopPropagation();
          const targetId = btn.getAttribute('aria-controls');
          const target = document.getElementById(targetId);
          if (!target) return;

          if (target.tagName === 'DRAWER-CONTENT') {
            if (target.hasAttribute('open')) {
              target.removeAttribute('open');
              btn.setAttribute('aria-expanded', 'false');
            } else {
              target.setAttribute('open', '');
              btn.setAttribute('aria-expanded', 'true');
              renderTrayGrid(drawerId, popoverId, index, getStrapSize());
            }
          } else if (target.tagName === 'POPOVER-CONTENT') {
            if (target.hasAttribute('hidden')) {
              target.removeAttribute('hidden');
              target.setAttribute('open', '');
              btn.setAttribute('aria-expanded', 'true');
              renderTrayGrid(drawerId, popoverId, index, getStrapSize());
            } else {
              target.setAttribute('hidden', '');
              target.removeAttribute('open');
              btn.setAttribute('aria-expanded', 'false');
            }
          }
        }, true);
      });
    }

    function rebuildSelectors() {
      buildSelectorList();
    }

    // Watch for strap size changes
    function watchStrapSize() {
      document.addEventListener('variant:changed', function(e) {
        if (!e.detail || !e.detail.variant) return;

        const variantId = e.detail.variant.id;
        let newSize = null;

        const sizeDataEl = document.getElementById('buckle-size-data-' + productId);
        if (sizeDataEl) {
          try {
            const sizeMap = JSON.parse(sizeDataEl.textContent);
            newSize = sizeMap[variantId.toString()] || null;
          } catch(e) {
            console.error('Error reading size map:', e);
          }
        }

        const sizeEl = document.getElementById('buckle-size-' + productId);
        if (sizeEl && newSize) sizeEl.value = newSize;

        // Update all existing selections to match new size
        const strapSize = newSize || getStrapSize();
        savedSelections[productId] = selections.map(function(selection) {
          if (!selection) return selection;
          const buckle = buckles.find(function(b) { return b.id === selection.buckleId; });
          if (!buckle) return selection;
          const variant = findMatchingVariant(buckle, selection.color, strapSize);
          if (variant) {
            return Object.assign({}, selection, {
              variantId: variant.id,
              image: variant.image || buckle.featured_image,
              label: buckle.title + ' — ' + selection.color + ' / ' + variant.size + ' — ' + formatMoney(variant.price)
            });
          }
          return null; // Size not available, remove selection
        }).filter(Boolean);
        selections = savedSelections[productId];
        rebuildSelectors();
      });
    }

    // Hook into Add to Cart
    function hookAddToCart() {
      let isAddingBuckles = false;
      let lastCartCount = 0;

      const cartCount = document.querySelector('cart-count');
      if (!cartCount) return;

      const cartObserver = new MutationObserver(function() {
        const newCount = parseInt(cartCount.textContent) || 0;
        const validSelections = selections.filter(Boolean);

        if (newCount > lastCartCount && validSelections.length > 0 && !isAddingBuckles) {
          lastCartCount = newCount;
          isAddingBuckles = true;

          const items = validSelections.map(function(s) {
            return { id: parseInt(s.variantId), quantity: 1 };
          });

          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: items })
          })
          .then(function(res) { return res.json(); })
          .then(function() {
            lastCartCount = newCount + validSelections.length;
            isAddingBuckles = false;
            // Reset all selections
            savedSelections[productId] = [];
            selections = savedSelections[productId];
            rebuildSelectors();
            document.dispatchEvent(new CustomEvent('cart:refresh'));
          })
          .catch(function(err) {
            console.error('Buckle add error:', err);
            isAddingBuckles = false;
          });
        } else {
          lastCartCount = newCount;
        }
      });

      cartObserver.observe(cartCount, { childList: true, subtree: true, characterData: true });
    }

    // Initialize
    buildSelectorList();
    watchStrapSize();
    hookAddToCart();
  }

  // Init on page load for product pages
  document.addEventListener('DOMContentLoaded', function() {
    document.querySelectorAll('.buckle-selector').forEach(function(el) {
      initBuckleSelector(el.dataset.productId);
    });
  });

  // Watch for quick-buy opening and re-initialize
  const quickBuyObserver = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      const isQuickBuy = mutation.type === 'attributes' &&
        mutation.attributeName === 'open' &&
        mutation.target.hasAttribute('open') &&
        (mutation.target.tagName === 'QUICK-BUY-POPOVER' ||
         mutation.target.tagName === 'QUICK-BUY-DRAWER');

      if (isQuickBuy) {
        const quickBuy = mutation.target;
        setTimeout(function() {
          quickBuy.querySelectorAll('.buckle-selector').forEach(function(el) {
            const productId = el.dataset.productId;
            initBuckleSelector(productId, quickBuy);
          });
        }, 300);

        // Also watch for product-rerender inside quick-buy
        const rerenderObserver = new MutationObserver(function() {
          // Disconnect while we re-initialize to prevent loop
          rerenderObserver.disconnect();
          
          setTimeout(function() {
            quickBuy.querySelectorAll('.buckle-selector').forEach(function(el) {
              const productId = el.dataset.productId;
              // Only re-initialize if list hasn't been built yet
              if (!el.querySelector('.buckle-selector__list')) {
                initBuckleSelector(productId, quickBuy);
              }
            });
            
            // Reconnect after initialization
            setTimeout(function() {
              if (rerender) {
                rerenderObserver.observe(rerender, { childList: true, subtree: true });
              }
            }, 500);
          }, 300);
        });

        const rerender = quickBuy.querySelector('product-rerender');
        if (rerender) {
          rerenderObserver.observe(rerender, { childList: true, subtree: true });
        }
      }
    });
  });

  quickBuyObserver.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['open']
  });
})();



// ============================================
// Move Judge.me widget after buckle selector
// ============================================
document.addEventListener('DOMContentLoaded', function() {
  const judgeMe = document.querySelector('[id*="judge_me_reviews_review_snippet"]');
  const buckleSelector = document.querySelector('.buckle-selector');

  if (judgeMe && buckleSelector) {
    // Insert Judge.me after buckle selector
    buckleSelector.after(judgeMe.closest('.shopify-app-block'));
  }
});


// ============================================
// Menu Item Tags and Icons
// ============================================
document.addEventListener('DOMContentLoaded', function() {
  function processMenuTags() {
    const selectors = [
      '.mega-menu__title',
      '.linklist__item .link--faded',
      '.nav-dropdown__link',
      '.mobile-nav__link'
    ];

    selectors.forEach(function(selector) {
      document.querySelectorAll(selector).forEach(function(link) {
        let text = link.textContent;

        const tagMatch = text.match(/\[(NEW|HOT|POPULAR|SALE|IN-STOCK|EARN)\]/i);

        const iconMatch = text.match(/\[icon:([\w-]+)\]/i);
        const solidMatch = text.match(/\[solid:([\w-]+)\]/i);

        
        if (!tagMatch && !iconMatch && !solidMatch) return;

        text = text
          .replace(/\[(NEW|HOT|POPULAR|SALE|IN-STOCK|EARN)\]/gi, '')
          .replace(/\[icon:[\w-]+\]/gi, '')
          .replace(/\[solid:[\w-]+\]/gi, '')
          .trim();

        let html = '';

        if (iconMatch) {
          html += `<i class="fa-kit ${iconMatch[1]}" style="margin-right: 6px;"></i>`;
        }
        if (solidMatch) {
          html += `<i class="fa-solid ${solidMatch[1]}" style="margin-right: 6px;"></i>`;
        }

        html += text;

        if (tagMatch) {
          const tag = tagMatch[1].toUpperCase();
          const styles = {
            'NEW':      { bg: '#9aff83', color: '#252628' },
            'POPULAR':      { bg: '#9aff83', color: '#252628' },
            'IN-STOCK': { bg: '#9aff83', color: '#252628' },
            'EARN': { bg: '#9aff83', color: '#252628' },
            'HOT':      { bg: '#FF5400', color: '#ffffff' },
            'SALE':     { bg: '#FF5400', color: '#ffffff' }
          };
          const s = styles[tag] || { bg: '#999', color: '#fff' };
          // html += ` <span style="
          //   display: inline-block;
          //   background: ${s.bg};
          //   color: ${s.color};
          //   font-size: 0.6em;
          //   font-weight: 700;
          //   padding: 2px 6px;
          //   border-radius: 20px;
          //   vertical-align: baseline;
          //   position: relative;
          //   top: -0.30em;
          //   letter-spacing: 0.05em;
          //   line-height: 1.4;
          //   text-transform: uppercase;
          // ">${tag}</span>`;
          html += ` <span style="
          display: inline-block;
          background: ${s.bg};
          color: ${s.color};
          font-size: 0.6em;
          font-weight: 700;
          padding: 2px 8px 2px 14px;
          clip-path: polygon(8px 0%, 100% 0%, 100% 100%, 8px 100%, 0% 50%);
          //clip-path: polygon(6px 0%, 100% 0%, 100% 100%, 6px 100%, 0% 70%, 0% 30%);
          position: relative;
          top: -0.30em;
          letter-spacing: 0.05em;
          line-height: 1.4;
          text-transform: uppercase;
          //border-radius: 0 3px 3px 0;
        ">${tag}</span>`;
        }

        link.innerHTML = html;
      });
    });
  }

  processMenuTags();

  // Re-run when mega menus become visible
  const megaMenuObserver = new MutationObserver(function() {
    processMenuTags();
  });
  document.querySelectorAll('.mega-menu').forEach(function(menu) {
    megaMenuObserver.observe(menu, { attributes: true, attributeFilter: ['hidden'] });
  });
});

// ============================================
// Prevent "Watch Enthusiasts" from wrapping
// ============================================
document.addEventListener('DOMContentLoaded', function() {
  document.querySelectorAll('h3.heading.h2').forEach(function(el) {
    if (el.textContent.includes('Watch Enthusiasts')) {
      el.innerHTML = el.innerHTML.replace(
        'Watch Enthusiasts',
        '<span style="white-space: nowrap;">Watch Enthusiasts</span>'
      );
    }
  });
});


// ============================================
// Footer copyright text replacement
// ============================================
document.addEventListener('DOMContentLoaded', function() {
  const copyright = document.querySelector('.footer__copyright');
  if (copyright) {
    copyright.innerHTML = '&copy; Tobler Watch Company, LLC. Wrist Outfitters and the Wrist Outfitters winged watch and plane logos are trademarks of Tobler Watch Co. All rights reserved.';
  }
});


// ============================================
// Custom notice disclaimer badge on product pages - determines if text is longer than width
// ============================================
// document.addEventListener('DOMContentLoaded', function () {
//   const wrap = document.getElementById('ct-notice-disclaimer-scroll-wrap');
//   const texts = document.querySelectorAll('.ct-notice-disclaimer-text');
//   const clone = document.querySelector('.ct-notice-clone');

//   if (!wrap || texts.length === 0) return;

//   // Force the wrap to a hard pixel width so it never bleeds into page layout
//   wrap.style.width = wrap.offsetWidth + 'px';
//   wrap.style.maxWidth = wrap.offsetWidth + 'px';

//   const firstText = texts[0];
//   const gap = 60;
//   const totalDistance = firstText.scrollWidth + gap;

//   texts.forEach(t => {
//     t.style.setProperty('--scroll-distance', `-${totalDistance}px`);
//     t.style.animationPlayState = 'running';
//   });
// });

function initNoticeDisclaimer() {
  const wrap = document.getElementById('ct-notice-disclaimer-scroll-wrap');
  const texts = document.querySelectorAll('.ct-notice-disclaimer-text');

  if (!wrap || texts.length === 0) return;

  // Reset animation so it restarts cleanly
  texts.forEach(t => {
    t.style.animationPlayState = 'paused';
    t.style.animation = 'none';
    void t.offsetWidth; // Force reflow to reset animation
    t.style.animation = '';
  });

  // Reset width lock before recalculating
  wrap.style.width = '';
  wrap.style.maxWidth = '';
  void wrap.offsetWidth;

  // Lock the wrap width
  wrap.style.width = wrap.offsetWidth + 'px';
  wrap.style.maxWidth = wrap.offsetWidth + 'px';

  const firstText = texts[0];
  const gap = 60;
  const totalDistance = firstText.scrollWidth + gap;

  texts.forEach(t => {
    t.style.setProperty('--scroll-distance', `-${totalDistance}px`);
    t.style.animationPlayState = 'running';
  });
}

function initWithDelay() {
  setTimeout(initNoticeDisclaimer, 100);
}

document.addEventListener('DOMContentLoaded', initNoticeDisclaimer);
document.addEventListener('product:rerender', initWithDelay);
document.addEventListener('variant:changed', initWithDelay);